/**
 * Vet-portal API client. Mirrors `customer_api.ts` but hits the /api/vet/*
 * endpoints with the kv_session cookie.
 */

export class VetApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message);
    this.name = 'VetApiError';
  }
}

export async function vetApi<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    throw new VetApiError(`API ${res.status}`, res.status, body);
  }
  return res.json();
}

export interface VetMeResponse {
  vet: {
    id: string;
    email: string;
    name: string | null;
  };
  pets: Array<{
    grantId: string;
    pet: {
      id: string;
      name: string;
      species: string;
      breed: string | null;
      dateOfBirth: string | null;
      sex: string | null;
      microchip: string | null;
    };
    kennel: { name: string; slug: string; accountId: string };
    grantedAt: string;
  }>;
}

export interface VetPetRecord {
  pet: {
    id: string;
    name: string;
    species: string;
    breed: string | null;
    dateOfBirth: string | null;
    sex: string | null;
    neutered: number | null;
    color: string | null;
    microchip: string | null;
    microchipRegisteredWith: string | null;
    weightKg: number | null;
    allergiesJson: string | null;
    medicationsJson: string | null;
    medicalConditions: string | null;
    behaviorNotes: string | null;
    specialInstructions: string | null;
    insuranceProvider: string | null;
    insurancePolicyNumber: string | null;
    insuranceExpiresOn: string | null;
    notes: string | null;
  };
  kennel: { accountId: string; name: string; slug: string };
  vaccinations: Array<{
    id: string;
    vaccineName: string | null;
    vaccineSlug: string | null;
    administeredOn: string | null;
    expiresOn: string | null;
    verifiedAt: string | null;
    notes: string | null;
  }>;
  incidents: Array<{
    id: string;
    incidentType: string;
    severity: string;
    occurredAt: string;
    description: string;
    actionTaken: string | null;
    vetCalled: boolean;
    followUpRequired: boolean;
    followUpNotes: string | null;
    status: string;
  }>;
}
